import { FC, useCallback, useEffect, useMemo } from "react";
import {
    AssortmentTaskType,
    Market,
    SubtaskFacings,
    SubtaskFinGroupPhotos,
    SubtaskProductsMatrix,
    TaskAssortmentMatrix,
} from "tm-shared/types";

import Header from "@/components/Header";
import { MainContainer } from "@/components/Layout/Styled";
import { SVScanButton } from "@/components/SVScanButton";
import { TASK_SCREEN_RECORDING_USER_IDS } from "@/config";
import { ASSORTMENT_SUBTASK_ICON } from "@/constants";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { useTaskConfigSelector } from "@/hooks/useTaskSelectors";
import { openReplayService } from "@/Services/OpenReplay";
import { setMdTasksConfig } from "@/store/slices/ui";

import { Facings } from "./Facings";
import { FacingsController } from "./Facings/FacingsController";
import { FinGroupPhotos } from "./FinGroupPhotos";
import { FinGroupPhotosController } from "./FinGroupPhotos/components/FinGroupPhotosController";
import { ProductsMatrix } from "./ProductsMatrix";
import { ProductsMatrixController } from "./ProductsMatrix/components/ProductsMatrixController";
import {
    StyledAssortmentMatrixHeader,
    StyledAssortmentMatrixStepCircleIcon,
    StyledAssortmentMatrixSteps,
} from "./Styled";

// Types
export type AMStepStatus = "active" | "finished" | "default";

type SubtaskUnion =
    | SubtaskProductsMatrix
    | SubtaskFinGroupPhotos
    | SubtaskFacings;

export interface AMStepData {
    key: string;
    icon: React.ReactNode;
    status: AMStepStatus;
    subtaskType: AssortmentTaskType;
    completed: boolean;
}

interface AssortmentMatrixPageProps {
    market: Market;
    task: TaskAssortmentMatrix;
}

type ComponentType = React.ComponentType<{
    subtask: SubtaskUnion;
    market: Market;
    task: TaskAssortmentMatrix;
}>;

// Constants

const COMPONENT_CONFIG: {
    controllers: Record<AssortmentTaskType, ComponentType>;
    content: Record<AssortmentTaskType, ComponentType>;
} = {
    controllers: {
        [AssortmentTaskType.ProductsMatrix]: ProductsMatrixController,
        [AssortmentTaskType.FinGroupPhotos]: FinGroupPhotosController,
        [AssortmentTaskType.Facings]: FacingsController,
    },
    content: {
        [AssortmentTaskType.ProductsMatrix]: ProductsMatrix,
        [AssortmentTaskType.FinGroupPhotos]: FinGroupPhotos,
        [AssortmentTaskType.Facings]: Facings,
    },
};

export const AssortmentMatrixPage: FC<AssortmentMatrixPageProps> = ({
    task,
    market,
}) => {
    const dispatch = useAppDispatch();
    const { id: userId } = useAppSelector((s) => s.user);

    const taskId = task.id;
    const marketId = market.id;

    useEffect(() => {
        if (TASK_SCREEN_RECORDING_USER_IDS.includes(userId)) {
            openReplayService.startTaskRecording(taskId);
        }
    }, []);

    // Selectors
    const { activeItem: activeStepKey } = useTaskConfigSelector({
        marketId,
        taskId,
    });

    const assortmentTaskPayload = useAppSelector(
        (s) => s.progress.assortmentMatrix[taskId],
    );

    const steps = useMemo<AMStepData[]>(() => {
        if (!task.context.subtasks.length) return [];

        const defaultActiveKey =
            activeStepKey ?? task.context.subtasks[0]?.subtaskId;

        return task.context.subtasks.map((subtask) => {
            const isCompleted =
                assortmentTaskPayload?.[subtask.subtaskId]?.completed ?? false;
            const isActive = subtask.subtaskId === defaultActiveKey;
            const Icon = ASSORTMENT_SUBTASK_ICON[subtask.type];

            return {
                key: subtask.subtaskId,
                icon: <Icon style={{ fontSize: 16 }} />,
                status: isActive ? "active" : "default",
                subtaskType: subtask.type as AssortmentTaskType,
                completed: isCompleted,
            };
        });
    }, [task.context.subtasks, assortmentTaskPayload, activeStepKey]);

    const activeStepData = useMemo(
        () => steps.find((step) => step.status === "active"),
        [steps],
    );

    const activeSubtask = useMemo(
        () =>
            activeStepData
                ? task.context.subtasks.find(
                      (subtask) => subtask.subtaskId === activeStepData.key,
                  )
                : null,
        [task.context.subtasks, activeStepData],
    );

    // Handlers
    const handleStepClick = useCallback(
        (stepKey: string) => {
            if (stepKey === activeStepData?.key) return;

            dispatch(
                setMdTasksConfig({
                    marketId,
                    taskId,
                    field: "activeItem",
                    value: stepKey,
                }),
            );
        },
        [activeStepData?.key, dispatch, marketId, taskId],
    );

    const stepperItems = useMemo(
        () =>
            steps.map((step) => ({
                key: step.key,
                icon: (
                    <StyledAssortmentMatrixStepCircleIcon
                        $active={step.status === "active"}
                        $finished={step.completed}
                        onClick={() => handleStepClick(step.key)}
                    >
                        {step.icon}
                    </StyledAssortmentMatrixStepCircleIcon>
                ),
            })),
        [steps, handleStepClick],
    );

    // Initialize active step if not set
    useEffect(() => {
        if (!activeStepKey && steps.length > 0) {
            const firstStepKey = steps[0]?.key;
            if (firstStepKey) {
                dispatch(
                    setMdTasksConfig({
                        marketId,
                        taskId,
                        field: "activeItem",
                        value: firstStepKey,
                    }),
                );
            }
        }
    }, [activeStepKey, steps, dispatch, marketId, taskId]);

    // Render functions
    const renderActiveController = useCallback(() => {
        if (!activeStepData || !activeSubtask) return null;

        const ControllerComponent =
            COMPONENT_CONFIG.controllers[activeStepData.subtaskType];

        return (
            <ControllerComponent
                subtask={activeSubtask}
                market={market}
                task={task}
            />
        );
    }, [activeStepData, activeSubtask, market, task]);

    const renderActiveContent = useCallback(() => {
        if (!activeStepData || !activeSubtask) return null;

        const ContentComponent =
            COMPONENT_CONFIG.content[activeStepData.subtaskType];

        return (
            <ContentComponent
                subtask={activeSubtask}
                market={market}
                task={task}
            />
        );
    }, [activeStepData, activeSubtask, market, task]);

    return (
        <>
            <Header
                back
                title={`Асортиментна матриця для ${market.contragent.name}`}
                rightActions={
                    <>
                        <SVScanButton marketId={marketId} taskId={taskId} />
                    </>
                }
            />

            {!task.context.subtasks.length ? (
                <MainContainer>
                    <div>Немає доступних підзадач</div>
                </MainContainer>
            ) : (
                <>
                    <StyledAssortmentMatrixHeader>
                        <StyledAssortmentMatrixSteps
                            size="small"
                            direction="horizontal"
                            responsive={false}
                            items={stepperItems}
                        />

                        {renderActiveController()}
                    </StyledAssortmentMatrixHeader>

                    {renderActiveContent()}
                </>
            )}
        </>
    );
};
